export type SpeechMode = 'dialogue' | 'monologue';
export type TimeMode = 'day' | 'night';
type TokenType = 'normal' | 'emphasis';

type LineToken = {
  text: string;
  type: TokenType;
};

export type StyledSegment = {
  text: string;
  style: string;
};

const NORMAL_COLOR_BY_MODE: Record<SpeechMode, string> = {
  dialogue: 'white',
  monologue: '#77e2f5',
};

const BORDER_COLOR_BY_TIME: Record<TimeMode, string> = {
  day: '#fec701',
  night: '#008ffe',
};

const BASE_STYLE = 'background: #3a3831; font-size: 13px ; text-decoration: none ;';
const EMPHASIS_BASE_STYLE =
  'background: #3a3831; color: #d39d00; font-size: 13px ; text-decoration: none ; text-shadow: 0.5px 0 0 #f4cc2e, -0.5px 0 0 #f4cc2e, 0 0.5px 0 #f4cc2e, 0 -0.5px 0 #f4cc2e; font-weight: bold;';

function tokenizeLine(line: string): LineToken[] {
  const tokens: LineToken[] = [];
  let emphasized = false;

  line.split('"').forEach((part, index) => {
    if (index > 0) {
      emphasized = !emphasized;
    }

    if (part.length > 0) {
      tokens.push({
        text: part,
        type: emphasized ? 'emphasis' : 'normal',
      });
    }
  });

  return tokens;
}

function getSingleLineStyle(timeMode: TimeMode): string {
  return `background: #3a3831; border-left: 10px solid ${BORDER_COLOR_BY_TIME[timeMode]} ; font-size: 13px ; padding: 3px 5px ; text-decoration: none ;`;
}

function getNormalSegmentStyle(
  index: number,
  total: number,
  mode: SpeechMode,
  timeMode: TimeMode,
): string {
  if (total === 1) {
    return `${getSingleLineStyle(timeMode)} color: ${NORMAL_COLOR_BY_MODE[mode]};`;
  }

  const border = index === 0 ? `border-left: 10px solid ${BORDER_COLOR_BY_TIME[timeMode]} ; ` : '';
  const padding =
    index === 0
      ? 'padding: 3px 0 3px 5px;'
      : index === total - 1
        ? 'padding: 3px 5px 3px 0 ;'
        : 'padding: 3px 0 3px 0;';

  return `${BASE_STYLE} ${border}${padding} color: ${NORMAL_COLOR_BY_MODE[mode]};`;
}

function getEmphasisSegmentStyle(index: number, total: number, timeMode: TimeMode): string {
  const border = index === 0 ? `border-left: 10px solid ${BORDER_COLOR_BY_TIME[timeMode]} ; ` : '';
  const padding =
    index === 0 && total === 1
      ? 'padding: 3px 5px;'
      : index === 0
        ? 'padding: 3px 0 3px 5px;'
        : index === total - 1
          ? 'padding: 3px 5px 3px 0;'
          : 'padding: 3px 0 3px 0;';

  return `${border}${EMPHASIS_BASE_STYLE} ${padding}`;
}

function renderLink(text: string, style: string): string {
  return `[${text}](#" style="${style})`;
}

export function convertLineToSegments(
  line: string,
  mode: SpeechMode,
  timeMode: TimeMode = 'day',
): StyledSegment[] {
  const tokens = tokenizeLine(line);
  const hasEmphasis = tokens.some((token) => token.type === 'emphasis');

  if (!hasEmphasis) {
    return [
      {
        text: line,
        style: `${getSingleLineStyle(timeMode)} color: ${NORMAL_COLOR_BY_MODE[mode]};`,
      },
    ];
  }

  return tokens.map((token, index) => {
      const style =
        token.type === 'emphasis'
          ? getEmphasisSegmentStyle(index, tokens.length, timeMode)
          : getNormalSegmentStyle(index, tokens.length, mode, timeMode);

      return {
        text: token.text,
        style,
      };
    });
}

function convertLine(line: string, mode: SpeechMode, timeMode: TimeMode): string {
  return convertLineToSegments(line, mode, timeMode)
    .map((segment) => renderLink(segment.text, segment.style))
    .join('');
}

export function convertRoll20ChatEffectPreview(
  input: string,
  mode: SpeechMode,
  timeMode: TimeMode = 'day',
): StyledSegment[][] {
  return input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => convertLineToSegments(line, mode, timeMode));
}

export function convertRoll20ChatEffect(
  input: string,
  mode: SpeechMode,
  timeMode: TimeMode = 'day',
): string {
  return input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => convertLine(line, mode, timeMode))
    .join('\n');
}
